import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const AgentApplicationsTable = ({ 
  applications = [], 
  onViewDocuments = () => {}, 
  onUpdateStatus = () => {},
  onBulkAction = () => {},
  selectedApplications = [],
  onSelectionChange = () => {}
}) => {
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const sortOptions = [
    { value: 'date', label: 'Application Date' },
    { value: 'business_name', label: 'Business Name' },
    { value: 'status', label: 'Status' },
    { value: 'reviewer', label: 'Reviewer' }
  ];

  const filteredApplications = applications?.filter(app => {
      const matchesStatus = filterStatus === 'all' || app?.status === filterStatus;
      const matchesSearch = app?.businessName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           app?.contactEmail?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      return matchesStatus && matchesSearch;
    })?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];
      
      if (sortBy === 'date') {
        aValue = new Date(a.submissionDate);
        bValue = new Date(b.submissionDate);
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(filteredApplications?.map(app => app?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectApplication = (applicationId, checked) => {
    if (checked) {
      onSelectionChange([...selectedApplications, applicationId]);
    } else {
      onSelectionChange(selectedApplications?.filter(id => id !== applicationId));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning text-warning-foreground',
      under_review: 'bg-primary text-primary-foreground',
      approved: 'bg-success text-success-foreground',
      rejected: 'bg-destructive text-destructive-foreground'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const getDocumentCompleteness = (documents) => {
    const required = ['incorporation', 'travel_authority', 'insurance', 'terms', 'privacy'];
    const submitted = Object.keys(documents)?.filter(key => documents?.[key]);
    return `${submitted?.length}/${required?.length}`;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      {/* Header with Filters */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Agent Applications</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {filteredApplications?.length} applications found
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="search"
              placeholder="Search by business name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full sm:w-64"
            />
            
            <Select
              options={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              placeholder="Filter by status"
              className="w-full sm:w-48"
            />
            
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              placeholder="Sort by"
              className="w-full sm:w-48"
            />
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedApplications?.length > 0 && (
          <div className="mt-4 p-4 bg-accent/10 rounded-lg border border-accent/20">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">
                {selectedApplications?.length} applications selected
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onBulkAction('approve', selectedApplications)}
                  iconName="Check"
                  iconSize={14}
                >
                  Approve Selected
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onBulkAction('reject', selectedApplications)}
                  iconName="X"
                  iconSize={14}
                >
                  Reject Selected
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <input
                  type="checkbox"
                  checked={selectedApplications?.length === filteredApplications?.length && filteredApplications?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">Business Details</th>
              <th className="text-left p-4 font-medium text-muted-foreground">Application Date</th>
              <th className="text-left p-4 font-medium text-muted-foreground">Documents</th>
              <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
              <th className="text-left p-4 font-medium text-muted-foreground">Reviewer</th>
              <th className="text-left p-4 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredApplications?.map((application) => (
              <tr key={application?.id} className="hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedApplications?.includes(application?.id)}
                    onChange={(e) => handleSelectApplication(application?.id, e?.target?.checked)}
                    className="rounded border-border"
                  />
                </td>
                
                <td className="p-4">
                  <div>
                    <div className="font-medium text-foreground">{application?.businessName}</div>
                    <div className="text-sm text-muted-foreground">{application?.contactEmail}</div>
                    <div className="text-sm text-muted-foreground">{application?.businessType}</div>
                  </div>
                </td>
                
                <td className="p-4">
                  <div className="text-sm text-foreground">
                    {new Date(application.submissionDate)?.toLocaleDateString('en-GB')}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(application.submissionDate)?.toLocaleTimeString('en-GB', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                </td>
                
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-foreground">
                      {getDocumentCompleteness(application?.documents)}
                    </span>
                    <div className="flex space-x-1">
                      {Object.entries(application?.documents)?.map(([type, submitted]) => (
                        <div
                          key={type}
                          className={`w-2 h-2 rounded-full ${
                            submitted ? 'bg-success' : 'bg-muted'
                          }`}
                          title={`${type}: ${submitted ? 'Submitted' : 'Missing'}`}
                        />
                      ))}
                    </div>
                  </div>
                </td>
                
                <td className="p-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(application?.status)}`}>
                    {application?.status?.replace('_', ' ')?.toUpperCase()}
                  </span>
                </td>
                
                <td className="p-4">
                  <div className="text-sm text-foreground">
                    {application?.assignedReviewer || 'Unassigned'}
                  </div>
                </td>
                
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDocuments(application)}
                      iconName="FileText"
                      iconSize={14}
                    >
                      Review
                    </Button>
                    
                    <div className="relative group">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="MoreVertical"
                        iconSize={14}
                      />
                      
                      <div className="absolute right-0 top-full mt-1 w-48 bg-popover border border-border rounded-lg shadow-elevated opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                        <div className="py-1">
                          <button
                            onClick={() => onUpdateStatus(application?.id, 'approved')}
                            className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted flex items-center space-x-2"
                          >
                            <Icon name="Check" size={14} className="text-success" />
                            <span>Approve</span>
                          </button>
                          <button
                            onClick={() => onUpdateStatus(application?.id, 'rejected')}
                            className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted flex items-center space-x-2"
                          >
                            <Icon name="X" size={14} className="text-destructive" />
                            <span>Reject</span>
                          </button>
                          <button
                            onClick={() => onUpdateStatus(application?.id, 'under_review')}
                            className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted flex items-center space-x-2"
                          >
                            <Icon name="Eye" size={14} className="text-primary" />
                            <span>Mark Under Review</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredApplications?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="FileX" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-medium text-foreground mb-2">No applications found</h3>
          <p className="text-muted-foreground">
            {searchTerm || filterStatus !== 'all' ?'Try adjusting your search or filter criteria' :'No agent applications have been submitted yet'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default AgentApplicationsTable;